import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fetchCompaniesService, deleteCompanyService } from 'services/modules/idp/company';
import { setCompanyData } from 'stores/modules/idp/authreducer';

import CapCompanies from './CapCompanies';

import { useNotification } from 'config/Notification';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ApartmentIcon from '@mui/icons-material/Apartment';
import WarningIcon from '@mui/icons-material/Warning';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  TablePagination,
  Divider,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment
} from '@mui/material';

const Companies = () => {
  // State variables
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [chooseDialog, setChooseDialog] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState({ name: '', location: '', id: '' });

  // Redux selectors
  const companyContainer = useSelector((state) => state.company);
  const authenticatedUserContainer = useSelector(state => state.auth.user);
  const companyDataContainer = useSelector((state) => state.company.companies);

  // Redux dispatch
  const dispatch = useDispatch();

  // React router
  const navigate = useNavigate();

  // Notification
  const { openNotification } = useNotification();

  // Method to fetch companies data
  const fetchCompaniesData = async () => {
    try {
      setLoadingData(true);
      const params = { page: page + 1, limit: rowsPerPage, sortBy: '-createdAt' };
      if (searchQuery.trim() !== '') {
        params.search = searchQuery.trim();
      } else {
        params.partner = null;
      }
      await dispatch(fetchCompaniesService({ params}));
    } catch (error) {
      setLoadingData(false);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchCompaniesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchQuery]);

  // Method to toggle the view of partner customer companies under partner company
  const toggleCompany = (companyId) => {
    setExpandedCompanies((prevState) => ({
      ...prevState,
      [companyId]: !prevState[companyId],
    }));
  };

  // Handles page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handles change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handles change in search query like company name,domain or location
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handles navigation to company profile component
  const handleViewCompany = async (company) => {
    let obj = {};
    obj.companyid = company.id;
    obj.companyname = company.name;
    obj.domain = company.domain;
    await dispatch(setCompanyData(obj));
    navigate(`/company-profile`);
  };

  // Handles navigation to users component
  const handleViewUsers = async (company) => {
    let obj = {};
    obj.companyid = company.id;
    obj.companyname = company.name;
    obj.domain = company.domain;
    await dispatch(setCompanyData(obj));
    navigate(`/ecs/users`);
  };

  // Handles navigation to edit company component
  const handleEditCompany = (companyId) => {
    navigate(`/company-edit/${companyId}`);
  };

  // Handles navigation to add company component based on permissions
  const handleAddCompany = () => {
    if (authenticatedUserContainer.roles.some(role => role.permissions.includes('GET_ALL_COMPANIES'))) {
      setChooseDialog(true);
    } else {
      navigate(`/add-company`);
    }
  };

  // Method to check whether the company has partner subscription or not
  const isPartnerSubscription = (subscriptionPlanSlug) => {
    if (subscriptionPlanSlug === null) {
      return;
    } else {
      return subscriptionPlanSlug.includes('partner');
    }
  };

  // This method is called when user clicks on delete company under actions
  const handleDeleteClick = (name, location, id) => {
    setCompanyToDelete({ name, location, id });
    setDeleteDialogOpen(true);
  };

  // Method to handle company deletion
  const handleDeleteConfirm = async (company_id) => {
    try {
      let deleteStatus = await deleteCompanyService(company_id);
      setDeleteDialogOpen(false);
      if (deleteStatus) {
        openNotification('Company deleted successfully', 'success');
        fetchCompaniesData();
      } else {
        openNotification('Error while deleting the company', 'error');
      }
    } catch (error) {
      openNotification('Error while deleting the company', 'error');
    }
  };

  // This condition handles navigation to capCompanies component if the user has subscribed to CAP service

  if (!(authenticatedUserContainer.roles.some(role => role.permissions.includes('GET_ALL_COMPANIES'))) && 
      (authenticatedUserContainer.company.applications.some(app => app.label === 'CAP'))) {
    return <CapCompanies />;
  }

  const filteredCompaniesBySearch = companyDataContainer?.results || [];
  return (
    <>
      <div className="m-2">
        <div className="row header-cont p-2">
          <div className="col-xs-12 col-sm-12 col-md-6 col-lg-3 partner-cont">
            <img src="../assets/partners.svg" className="card-icon" alt="logo" />
            <span className="icon-color text-h5 text-bold font">Partners</span>
          </div>
          {authenticatedUserContainer &&
            authenticatedUserContainer.roles &&
            authenticatedUserContainer.roles.some((role) =>
              role.permissions.includes('CREATE_CHILD_COMPANIES')
            ) && (
              <div className="col-xs-12 col-sm-6 col-md-6 col lg-3 cont">
                <Button variant="contained" className="add-company font" onClick={() => handleAddCompany()}>Add company</Button>
              </div>
            )}
        </div>
        <Divider className="divider" />
        <div className="col lg-4 col-md-4 col-sm-12 col-xs-12 search">
          <TextField
            variant="outlined"
            placeholder="Search"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="card-overflow">
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Organization</TableCell>
                  <TableCell>Domain URL</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingData ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress className="text-blueviolet" />
                    </TableCell>
                  </TableRow>
                ) : filteredCompaniesBySearch.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <WarningIcon color="black" fontSize="small" />
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCompaniesBySearch.map((company) => (
                    <React.Fragment key={company.id}>
                      <TableRow>
                        <TableCell>
                          <div className="flex cell-items">
                            <div className="business-container">
                              <BusinessIcon className="business" />
                            </div>
                            <span>{company.name}</span>
                            {isPartnerSubscription(company && company.subscriptionPlan && company.subscriptionPlan.slug) && (
                              <Tooltip title="Partner Company" placement="top" >
                                <div className="partner-company-container">
                                  <LocalOfferIcon fontSize="small" className='partner' />
                                </div>
                              </Tooltip>
                            )}
                            {company.partner && (
                              <Tooltip title="Partner Customer" placement="top">
                                <div className="partner-customer-container">
                                  <ApartmentIcon fontSize="small" className="partner" />
                                </div>
                              </Tooltip>
                            )}
                            {company.customers && company.customers.length > 0 && (
                              <span
                                onClick={() => toggleCompany(company.id)}
                              >
                                {expandedCompanies[company.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-secondary">{company.domain}</TableCell>
                        <TableCell>{`+${company.mobile.code} ${company.mobile.digits}`}</TableCell>
                        <TableCell className="table-email">{company.email}</TableCell>
                        <TableCell>
                          <Tooltip title="View Users" placement="top" >
                            <div className="users-container">
                              <GroupIcon className='usersandediticon' onClick={() => handleViewUsers(company)} />
                            </div>
                          </Tooltip>
                          <Tooltip title="View Company" placement="top">
                            <img src="../assets/viewicon.svg" alt="View" className='icons-style' onClick={() => handleViewCompany(company)} />
                          </Tooltip>
                          {authenticatedUserContainer &&
                            authenticatedUserContainer.roles &&
                            authenticatedUserContainer.roles.some((role) =>
                              role.permissions.includes('UPDATE_SELF_COMPANY')
                            ) && (
                              <Tooltip title="Edit Company" placement="top">
                                <div className="edit-container" onClick={() => handleEditCompany(company.id)}>
                                  <EditIcon className='usersandediticon' />
                                </div>
                              </Tooltip>
                            )}
                          {authenticatedUserContainer &&
                            authenticatedUserContainer.roles &&
                            authenticatedUserContainer.roles.some((role) =>
                              role.permissions.includes('DELETE_ANY_COMPANIES')
                            ) && (
                              <Tooltip title="Delete Company" placement="top">
                                <img src="../assets/deleteicon.svg" alt="Delete" className='icons-style' onClick={() => handleDeleteClick(company.name, '', company.id)} />
                              </Tooltip>
                            )}
                        </TableCell>
                      </TableRow>
                      {expandedCompanies[company.id] &&
                        company.customers &&
                        company.customers.map((subCompany) => (
                          <TableRow key={subCompany.id}>
                            <TableCell>
                              <div className="flex cell-items">
                                <div className="business-container">
                                  <BusinessIcon className="business" />
                                </div>
                                {subCompany.name}
                                <Tooltip title="Partner Customer" placement="top">
                                  <div className="partner-customer-container">
                                    <ApartmentIcon fontSize="small" className="partner" />
                                  </div>
                                </Tooltip>
                              </div>
                            </TableCell>
                            <TableCell className="text-secondary">{subCompany.domain}</TableCell>
                            <TableCell>{`+${subCompany.mobile.code} ${subCompany.mobile.digits}`}</TableCell>
                            <TableCell className="table-email">{subCompany.email}</TableCell>
                            <TableCell>
                              <Tooltip title="View Users" placement="top" >
                                <div className="users-container">
                                  <GroupIcon className='usersandediticon' onClick={() => handleViewUsers(subCompany)} />
                                </div>
                              </Tooltip>
                              <Tooltip title="View Company" placement="top">
                                <img src="../assets/viewicon.svg" alt="View" className='icons-style' onClick={() => handleViewCompany(subCompany)} />
                              </Tooltip>
                              {authenticatedUserContainer &&
                                authenticatedUserContainer.roles &&
                                authenticatedUserContainer.roles.some((role) =>
                                  role.permissions.includes('UPDATE_SELF_COMPANY')
                                ) && (
                                  <Tooltip title="Edit Company" placement="top">
                                    <div className="edit-container">
                                      <EditIcon className='usersandediticon' onClick={() => handleEditCompany(subCompany.id)} />
                                    </div>
                                  </Tooltip>
                                )}
                              {authenticatedUserContainer &&
                                authenticatedUserContainer.roles &&
                                authenticatedUserContainer.roles.some((role) =>
                                  role.permissions.includes('DELETE_ANY_COMPANIES')
                                ) && (
                                  <Tooltip title="Delete Company" placement="top">
                                    <img src="../assets/deleteicon.svg" alt="Delete" className='icons-style' onClick={() => handleDeleteClick(subCompany.name, '', subCompany.id)} />
                                  </Tooltip>
                                )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
  
          <TablePagination
            rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]}
            component="div"
            count={companyContainer.companies.totalResults ? companyContainer.companies.totalResults : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </div>
      <Dialog
        open={chooseDialog}
        onClose={() => setChooseDialog(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle align="center">
          Choose customer type
          <Divider className="divider" />
        </DialogTitle>
        <DialogActions className="m-3">
          <Button variant="contained" onClick={() => navigate(`/add-partnercompany`, { state: { customertype: 'Partner' } })} >
            Partner
          </Button>
          <Button variant="contained" onClick={() => navigate(`/add-partnercompany`, { state: { customertype: 'endcustomer' } })} >
            End customer
          </Button>
        </DialogActions>
      </Dialog>
  
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle align="center">
          Are you sure you want to delete the company?
          <IconButton edge="end" color="inherit" onClick={() => setDeleteDialogOpen(false)} >
            <CloseIcon />
          </IconButton>
          <Divider className="divider" />
        </DialogTitle>
  
        <DialogContent className="dialogBox">
          <div className="dived-card">
            <BusinessIcon className="delete-company-dialog-icon" />
            <div className="m-2">
              <h4>{companyToDelete.name}</h4>
              <h4>{companyToDelete.location}</h4>
            </div>
          </div>
        </DialogContent>
        <DialogActions className="m-3">
          <Button onClick={() => setDeleteDialogOpen(false)} variant="contained" className="btn-card">
            Cancel
          </Button>
          <Button onClick={() => handleDeleteConfirm(companyToDelete.id)} variant="contained" className="btn-card danger" >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Companies;